/**
 * UrlResearchSection Component
 * Allows users to generate knowledge files from web URLs.
 * Supports two modes: saving to a data bundle OR to a bot's context files.
 * States: idle → input → loading → result
 */

import React, { useState, useRef } from 'react';
import { message } from 'antd';
import { useLanguage } from '../../../hooks/useLanguage';
import { DataBundleApi } from '../services/dataBundleService';
import type { DataBundleFile } from '../types';
import './UrlResearchSection.css';

type ResearchState = 'idle' | 'input' | 'loading' | 'result';

interface BundleModeProps {
  mode: 'bundle';
  bundleId: string;
  onFileSaved: (file: DataBundleFile) => void;
}

interface BotModeProps {
  mode: 'bot';
  botId: string;
  onFileSaved: () => void;
}

interface TenantModeProps {
  mode: 'tenant';
  tenantId: string;
  onFileSaved: () => void;
}

interface FolderModeProps {
  mode: 'folder';
  folderId: string;
  onFileSaved: () => void;
}

type UrlResearchSectionProps = BundleModeProps | BotModeProps | TenantModeProps | FolderModeProps;

export const UrlResearchSection: React.FC<UrlResearchSectionProps> = (props) => {
  const { t } = useLanguage();
  const [state, setState] = useState<ResearchState>('idle');
  const [url, setUrl] = useState('');
  const [goal, setGoal] = useState('');
  const [navigationHint, setNavigationHint] = useState('');
  const [guidance, setGuidance] = useState('');
  const [resultContent, setResultContent] = useState('');
  const [resultTitle, setResultTitle] = useState('');
  const [pagesCrawled, setPagesCrawled] = useState(0);
  const [sources, setSources] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [editorSourcesExpanded, setEditorSourcesExpanded] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleRun = async () => {
    if (!url.trim()) {
      message.warning(t('research.urlRequired') || 'Please enter a URL');
      return;
    }
    if (!goal.trim()) {
      message.warning(t('research.goalRequired') || 'Please describe what you want to extract');
      return;
    }

    setState('loading');
    abortControllerRef.current = new AbortController();

    try {
      const result = await DataBundleApi.researchUrl(
        url.trim(),
        goal.trim(),
        navigationHint.trim(),
        guidance.trim(),
      );
      setResultContent(result.content);
      setResultTitle(result.title);
      setPagesCrawled(result.pages_crawled);
      setSources(result.sources);
      setState('result');
    } catch (error: any) {
      if (error?.name === 'CanceledError' || error?.message === 'canceled') {
        setState('input');
        return;
      }
      console.error('Research error:', error);
      const msg = error?.response?.data?.error || t('research.failed');
      message.error(msg);
      setState('input');
    }
  };

  const resetState = () => {
    setState('idle');
    setUrl('');
    setGoal('');
    setNavigationHint('');
    setGuidance('');
    setResultContent('');
    setResultTitle('');
  };

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    resetState();
  };

  const handleSave = async () => {
    if (!resultContent.trim() || !resultTitle.trim()) return;

    setSaving(true);
    try {
      if (props.mode === 'bundle') {
        const result = await DataBundleApi.saveResearchToBundle(
          resultContent,
          resultTitle,
          props.bundleId,
          url,
          sources
        );
        props.onFileSaved(result.bundle_file);
      } else if (props.mode === 'folder') {
        await DataBundleApi.saveResearchToFolder(
          resultContent,
          resultTitle,
          props.folderId,
          url,
          sources
        );
        props.onFileSaved();
      } else if (props.mode === 'tenant') {
        await DataBundleApi.saveResearchToTenant(
          resultContent,
          resultTitle,
          props.tenantId,
          url
        );
        props.onFileSaved();
      } else {
        await DataBundleApi.saveResearchToBot(
          resultContent,
          resultTitle,
          props.botId,
          url
        );
        props.onFileSaved();
      }
      message.success(t('research.saved') || 'File saved successfully');
      resetState();
    } catch (error: any) {
      console.error('Save error:', error);
      const msg = error?.response?.data?.error || t('research.failedToSaveFile');
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([resultContent], { type: 'text/markdown' });
    const downloadUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = resultTitle.endsWith('.md') ? resultTitle : `${resultTitle}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(downloadUrl);
  };

  const handleRetry = () => {
    setResultContent('');
    setResultTitle('');
    setState('input');
  };

  const saveLabel = props.mode === 'bundle'
    ? (t('research.saveToBundle') || 'Save to Bundle')
    : props.mode === 'folder'
    ? (t('research.saveToLibrary') || 'Save to Library')
    : props.mode === 'tenant'
    ? (t('research.saveToTenantContext') || 'Save to Tenant Context')
    : (t('research.saveToAgent') || 'Save to Agent');

  // Idle state — just the button
  if (state === 'idle') {
    return (
      <button
        type="button"
        className="btn-url-research"
        onClick={() => setState('input')}
      >
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        {t('research.addFromUrl') || 'Add from URL'}
      </button>
    );
  }

  // Input state — form with three guided fields
  if (state === 'input') {
    return (
      <div className="url-research-form">
        <div className="url-research-header">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="2" y1="12" x2="22" y2="12" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          <span>{t('research.addFromUrl') || 'Add from URL'}</span>
        </div>

        <div className="url-research-field">
          <label className="url-research-label">
            {t('research.urlLabel') || 'Website URL'}
            <span className="url-research-required">*</span>
          </label>
          <input dir="auto"
            type="url"
            className="url-research-input"
            placeholder={t('research.urlPlaceholder') || 'https://example.com'}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoFocus
          />
        </div>

        <div className="url-research-field">
          <label className="url-research-label">
            {t('research.goalLabel') || 'What do you need?'}
            <span className="url-research-required">*</span>
          </label>
          <p className="url-research-help">
            {t('research.goalHelp') || 'Describe the information you want to extract from this site.'}
          </p>
          <textarea dir="auto"
            className="url-research-prompt"
            placeholder={t('research.goalPlaceholder') || 'e.g. All pricing tiers and the features included in each one'}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            rows={2}
          />
        </div>

        <div className="url-research-field">
          <label className="url-research-label">
            {t('research.navigationLabel') || 'Where to look'}
            <span className="url-research-optional">{t('research.optional') || 'optional'}</span>
          </label>
          <p className="url-research-help">
            {t('research.navigationHelp') || 'Sections to focus on or skip — helps us pick the right inner pages.'}
          </p>
          <textarea dir="auto"
            className="url-research-prompt"
            placeholder={t('research.navigationPlaceholder') || 'e.g. Focus on /pricing and /features. Skip blog posts and case studies.'}
            value={navigationHint}
            onChange={(e) => setNavigationHint(e.target.value)}
            rows={2}
          />
        </div>

        <div className="url-research-field">
          <label className="url-research-label">
            {t('research.guidanceLabel') || 'How to format'}
            <span className="url-research-optional">{t('research.optional') || 'optional'}</span>
          </label>
          <p className="url-research-help">
            {t('research.guidanceHelp') || 'Tone, structure, and special instructions for the output.'}
          </p>
          <textarea dir="auto"
            className="url-research-prompt"
            placeholder={t('research.guidancePlaceholder') || 'e.g. Output as a comparison table. Ignore marketing language.'}
            value={guidance}
            onChange={(e) => setGuidance(e.target.value)}
            rows={2}
          />
        </div>

        <div className="url-research-actions">
          <button
            type="button"
            className="btn-research-cancel"
            onClick={handleCancel}
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            type="button"
            className="btn-research-run"
            onClick={handleRun}
            disabled={!url.trim() || !goal.trim()}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5 3 19 12 5 21 5 3" />
            </svg>
            {t('research.run') || 'Run'}
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (state === 'loading') {
    return (
      <div className="url-research-loading">
        <div className="url-research-spinner" />
        <div className="url-research-loading-text">
          <span className="url-research-loading-title">
            {t('research.researching') || 'Researching...'}
          </span>
          <span dir="auto" className="url-research-loading-url">{url}</span>
        </div>
        <button
          type="button"
          className="btn-research-cancel"
          onClick={handleCancel}
        >
          {t('common.cancel') || 'Cancel'}
        </button>
      </div>
    );
  }

  // Result state — compact bar + sources dropdown + full-screen editor overlay
  return (
    <>
      <div className={`url-research-result-container ${sourcesExpanded && sources.length > 0 ? 'sources-open' : ''}`}>
      <div className="url-research-result-bar">
        <div className="url-research-result-meta">
          <button
            type="button"
            className="url-research-pages-btn"
            onClick={() => setSourcesExpanded(!sourcesExpanded)}
            title={sourcesExpanded ? t('dataBundles.hideCrawledPages') : t('dataBundles.showCrawledPages')}
          >
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"
              style={{ transform: sourcesExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
              <polyline points="9 18 15 12 9 6" />
            </svg>
            {pagesCrawled} {pagesCrawled === 1 ? 'page' : 'pages'} {t('research.crawled') || 'crawled'}
          </button>
          <span dir="auto" className="url-research-result-title">{resultTitle}</span>
        </div>
        <div className="url-research-result-bar-actions">
          <button
            type="button"
            className="btn-research-view"
            onClick={() => setEditorOpen(true)}
          >
            {t('research.viewEdit') || 'View & Edit'}
          </button>
          <button
            type="button"
            className="btn-research-save-bar"
            onClick={handleSave}
            disabled={saving || !resultContent.trim() || !resultTitle.trim()}
          >
            {saving ? (t('research.saving') || 'Saving...') : saveLabel}
          </button>
          <button
            type="button"
            className="btn-research-discard"
            onClick={resetState}
          >
            {t('research.discard') || 'Discard'}
          </button>
        </div>
      </div>
      {sourcesExpanded && sources.length > 0 && (
        <div className="url-research-sources-list">
          {sources.map((src, i) => (
            <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="url-research-source-link">
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              {src}
            </a>
          ))}
        </div>
      )}
      </div>

      {editorOpen && (
        <div className="url-research-editor-overlay" onClick={() => setEditorOpen(false)}>
          <div className="url-research-editor" onClick={(e) => e.stopPropagation()}>
            <div className="url-research-editor-header">
              <input dir="auto"
                type="text"
                className="url-research-editor-title"
                placeholder={t('research.documentTitle') || 'Document Title'}
                value={resultTitle}
                onChange={(e) => setResultTitle(e.target.value)}
              />
              <button
                type="button"
                className="url-research-editor-close"
                onClick={() => setEditorOpen(false)}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <textarea dir="auto"
              className="url-research-editor-content"
              value={resultContent}
              onChange={(e) => setResultContent(e.target.value)}
            />

            {/* Sources list in editor — collapsed by default */}
            {sources.length > 0 && (
              <div className="url-research-editor-sources">
                <button
                  type="button"
                  className="url-research-editor-sources-title"
                  onClick={() => setEditorSourcesExpanded(!editorSourcesExpanded)}
                >
                  <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2"
                    style={{ transform: editorSourcesExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                  {sources.length} {sources.length === 1 ? 'page' : 'pages'} crawled
                </button>
                {editorSourcesExpanded && sources.map((src, i) => (
                  <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="url-research-source-link">
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                    {src}
                  </a>
                ))}
              </div>
            )}

            <div className="url-research-editor-footer">
              <div className="url-research-editor-actions">
                <button type="button" className="btn-research-retry" onClick={handleRetry}>
                  {t('research.retry') || 'Retry'}
                </button>
                <button type="button" className="btn-research-download" onClick={handleDownload}>
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {t('research.download') || 'Download'}
                </button>
                <button
                  type="button"
                  className="btn-research-save"
                  onClick={handleSave}
                  disabled={saving || !resultContent.trim() || !resultTitle.trim()}
                >
                  {saving ? (t('research.saving') || 'Saving...') : saveLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
